import React, { useState } from 'react'
import { confirmAlert } from 'react-confirm-alert'
import 'react-confirm-alert/src/react-confirm-alert.css'
import './parchmentConfirm.css'

function cleanupConfirmDom() {
  document.body.classList.remove('react-confirm-alert-body-element')
  document.body.style.overflow = ''
  document.querySelectorAll('.react-confirm-alert-overlay').forEach((el) => {
    try {
      el.remove()
    } catch {
      /* ignore */
    }
  })
}

/**
 * Confirmación con look pergamino (sustituye el diálogo default de react-confirm-alert).
 *
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.message
 * @param {string} [opts.cancelLabel='Cancelar']
 * @param {string} [opts.confirmLabel='Confirmar']
 * @param {'danger'|'default'} [opts.tone='default']
 * @param {() => void|Promise<void>} opts.onConfirm
 * @param {() => void} [opts.onCancel]
 */
export function parchmentConfirm({
  title,
  message,
  cancelLabel = 'Cancelar',
  confirmLabel = 'Confirmar',
  tone = 'default',
  onConfirm,
  onCancel,
}) {
  confirmAlert({
    closeOnEscape: true,
    closeOnClickOutside: true,
    customUI: ({ onClose }) => {
      const finish = () => {
        try {
          onClose()
        } catch {
          /* ignore */
        }
        cleanupConfirmDom()
      }

      function ConfirmBody() {
        const [busy, setBusy] = useState(false)

        return (
          <div
            className={`parchment-confirm parchment-confirm--${tone}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="parchment-confirm-title"
          >
            <div className="parchment-confirm__seal" aria-hidden="true">
              {tone === 'danger' ? '✕' : '記'}
            </div>
            <h2 id="parchment-confirm-title" className="parchment-confirm__title">
              {title}
            </h2>
            {message ? <p className="parchment-confirm__message">{message}</p> : null}
            <div className="parchment-confirm__actions">
              <button
                type="button"
                className="parchment-confirm__btn parchment-confirm__btn--cancel"
                disabled={busy}
                onClick={() => {
                  onCancel?.()
                  finish()
                }}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className={`parchment-confirm__btn parchment-confirm__btn--confirm${
                  tone === 'danger' ? ' parchment-confirm__btn--danger' : ''
                }`}
                disabled={busy}
                onClick={() => {
                  if (busy) return
                  setBusy(true)
                  Promise.resolve(onConfirm?.())
                    .catch(() => {})
                    .finally(() => {
                      finish()
                    })
                }}
              >
                {busy ? 'Un momento…' : confirmLabel}
              </button>
            </div>
          </div>
        )
      }

      return <ConfirmBody />
    },
  })
}

/**
 * Prompt pergamino con campo de texto (p. ej. nota de cierre).
 *
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} [opts.message]
 * @param {string} [opts.placeholder]
 * @param {string} [opts.defaultValue='']
 * @param {boolean} [opts.required=false]
 * @param {string} [opts.cancelLabel='Cancelar']
 * @param {string} [opts.confirmLabel='Confirmar']
 * @param {'danger'|'default'} [opts.tone='default']
 * @param {(value: string) => void|Promise<void>} opts.onConfirm
 * @param {() => void} [opts.onCancel]
 */
export function parchmentPrompt({
  title,
  message,
  placeholder = '',
  defaultValue = '',
  required = false,
  cancelLabel = 'Cancelar',
  confirmLabel = 'Confirmar',
  tone = 'default',
  onConfirm,
  onCancel,
}) {
  confirmAlert({
    closeOnEscape: true,
    closeOnClickOutside: false,
    customUI: ({ onClose }) => {
      const finish = () => {
        try {
          onClose()
        } catch {
          /* ignore */
        }
        cleanupConfirmDom()
      }

      function PromptBody() {
        const [busy, setBusy] = useState(false)
        const [value, setValue] = useState(String(defaultValue || ''))
        const [error, setError] = useState(null)

        return (
          <div
            className={`parchment-confirm parchment-confirm--${tone}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="parchment-prompt-title"
          >
            <div className="parchment-confirm__seal" aria-hidden="true">
              {tone === 'danger' ? '✕' : '記'}
            </div>
            <h2 id="parchment-prompt-title" className="parchment-confirm__title">
              {title}
            </h2>
            {message ? <p className="parchment-confirm__message">{message}</p> : null}
            <textarea
              className="parchment-confirm__textarea"
              value={value}
              placeholder={placeholder}
              rows={3}
              disabled={busy}
              onChange={(e) => {
                setValue(e.target.value)
                if (error) setError(null)
              }}
            />
            {error ? <p className="parchment-confirm__error">{error}</p> : null}
            <div className="parchment-confirm__actions">
              <button
                type="button"
                className="parchment-confirm__btn parchment-confirm__btn--cancel"
                disabled={busy}
                onClick={() => {
                  onCancel?.()
                  finish()
                }}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className={`parchment-confirm__btn parchment-confirm__btn--confirm${
                  tone === 'danger' ? ' parchment-confirm__btn--danger' : ''
                }`}
                disabled={busy}
                onClick={() => {
                  if (busy) return
                  const trimmed = String(value || '').trim()
                  if (required && !trimmed) {
                    setError('Escribe una nota')
                    return
                  }
                  setBusy(true)
                  Promise.resolve(onConfirm?.(trimmed))
                    .catch(() => {})
                    .finally(() => {
                      finish()
                    })
                }}
              >
                {busy ? 'Un momento…' : confirmLabel}
              </button>
            </div>
          </div>
        )
      }

      return <PromptBody />
    },
  })
}
